import { Controller } from '@hotwired/stimulus'
import swapText from '../utils/swap_text'
import visibilityLoop from '../utils/visibility_loop'

// Drives the delivery-tracking card on the Uber Direct feature page.
//
// The courier follows the real route with SVG <animateMotion> + <mpath>, so it
// tracks the streets exactly rather than approximating them with translate stops.
// The route is one path: the courier drives to the restaurant, waits there
// while the order is collected, then carries it to the house — both legs at
// the same speed.
// SMIL can't be switched off from CSS, so the animation begins as "indefinite"
// and is started here — which also lets prefers-reduced-motion land on the
// finished state instead of a moving marker.
//
// The tracker steps advance on the same clock, so the card actually reaches
// "Delivered" instead of sitting on "On the way" forever.
//
//   <div data-controller="delivery-route">
//     <span data-delivery-route-target="status">…</span>
//     <animateMotion data-delivery-route-target="motion" begin="indefinite" …>
//     <li data-delivery-route-target="step">…</li>
//
export default class extends Controller {
  static targets = ['motion', 'trace', 'step', 'status', 'eta']
  static values = {
    // How long the finished state holds before the trip replays.
    hold: { type: Number, default: 3000 }
  }

  // Status line for each step index, so the headline and the step list can
  // never disagree about where the order is.
  //
  // `at` is how far into the run the step lands, as a fraction of the duration.
  // Both come from the <animateMotion> itself (`dur` and `keyTimes`): the
  // courier reaches the restaurant at the second keyTime and pulls away at the
  // third, so the card says "Picked up" for exactly as long as the marker sits
  // on the pin, and retuning the SVG can't desync the labels. The `at` values
  // here are only the fallback for markup without keyTimes.
  STATUSES = [
    { label: 'Order received', eta: '32 min away', at: 0 },
    { label: 'Picked up', eta: '21 min away', at: 0.2757 },
    { label: 'Out for delivery', eta: '9 min away', at: 0.4142 },
    { label: 'Delivered', eta: 'Just now', at: 1 }
  ]

  connect() {
    this.timers = []
    this.readMotionTiming()

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.settle()
      return
    }

    this.stopLoop = visibilityLoop(this.element, {
      start: () => this.run(),
      stop: () => this.clearTimers()
    })
  }

  disconnect() {
    if (this.stopLoop) this.stopLoop()
    this.clearTimers()
  }

  // Takes the run length and step fractions off the SMIL element so there is
  // one source of truth for the timing.
  readMotionTiming() {
    this.duration = 6500
    if (!this.hasMotionTarget) return

    const dur = parseFloat(this.motionTarget.getAttribute('dur'))
    if (dur > 0) this.duration = dur * 1000

    const keyTimes = (this.motionTarget.getAttribute('keyTimes') || '')
      .split(';')
      .map(parseFloat)
    if (keyTimes.length === this.STATUSES.length && keyTimes.every((t) => t >= 0)) {
      this.STATUSES.forEach((status, i) => (status.at = keyTimes[i]))
    }
  }

  // Park the card on its delivered state: no motion, but never a half-finished
  // route or a status that contradicts the steps.
  settle() {
    this.element.classList.add('is-settled')
    this.applyStep(this.STATUSES.length - 1)
  }

  run() {
    this.element.classList.remove('is-settled')
    this.element.classList.add('is-running')

    // Restart the trace stroke: removing and re-adding the class alone won't
    // replay a CSS animation without a reflow between the two.
    if (this.hasTraceTarget) {
      this.traceTarget.style.animation = 'none'
      void this.traceTarget.getBoundingClientRect()
      this.traceTarget.style.animation = ''
    }

    if (this.hasMotionTarget && typeof this.motionTarget.beginElement === 'function') {
      try {
        this.motionTarget.beginElement()
      } catch (_e) {
        // A browser without SMIL leaves the courier parked where it started;
        // the steps and the traced route still tell the story.
      }
    }

    this.STATUSES.forEach((status, index) => {
      this.timers.push(setTimeout(() => this.applyStep(index), status.at * this.duration))
    })

    this.timers.push(
      setTimeout(() => {
        this.clearTimers()
        this.run()
      }, this.duration + this.holdValue)
    )
  }

  applyStep(index) {
    this.stepTargets.forEach((step, i) => {
      step.classList.toggle('is-done', i < index)
      step.classList.toggle('is-current', i === index)
    })

    const status = this.STATUSES[index]
    if (!status) return

    if (this.hasStatusTarget) swapText(this.statusTarget, status.label)
    if (this.hasEtaTarget) swapText(this.etaTarget, status.eta)
    this.element.classList.toggle('is-delivered', index === this.STATUSES.length - 1)
  }

  clearTimers() {
    this.timers.forEach((t) => clearTimeout(t))
    this.timers = []
  }
}
